// Your own character: moved instantly on your screen, then quietly corrected when the server's answer arrives.
import { SUB } from '../../../shared/constants.js';
import type { ZoneMap } from '../../../shared/map.js';
import { cloneMove, stepMove, type MoveState } from '../../../shared/sim.js';
import { pushOutOfCircles } from '../../../shared/battle.js';

export class Predictor {
  state: MoveState;
  prev: MoveState;
  seq: number;
  private pending: { seq: number; bits: number }[] = [];
  private lastAck = -1;
  /** Visual offset (sub-pixels) that hides small corrections; fades to zero. */
  smoothX = 0; smoothY = 0; smoothZ = 0;
  corrections = 0; snaps = 0; lastError = 0;

  constructor(state: MoveState, seq: number, private map: ZoneMap) {
    this.state = cloneMove(state); this.prev = cloneMove(state); this.seq = seq;
  }

  /** One simulation tick with this input. Returns the tick's sequence number. */
  tick(bits: number): number {
    this.prev = cloneMove(this.state);
    this.seq++;
    this.step(this.state, bits);
    this.pending.push({ seq: this.seq, bits });
    if (this.pending.length > 600) this.pending.shift();
    return this.seq;
  }

  /** The server processed inputs up to `ack` and ended at `auth`. Replay what it hasn't seen yet. */
  reconcile(ack: number, auth: MoveState): void {
    if (ack < this.lastAck) return;   // an older snapshot arriving late
    this.lastAck = ack;
    while (this.pending.length && this.pending[0].seq <= ack) this.pending.shift();
    const s = cloneMove(auth);
    for (const p of this.pending) this.step(s, p.bits);
    const ex = this.state.x - s.x, ey = this.state.y - s.y, ez = this.state.z - s.z;
    if (ez) { this.smoothZ += ez; this.prev.z -= ez; if (!ex && !ey) this.corrections++; }
    if (ex || ey) {
      this.corrections++; this.lastError = Math.hypot(ex, ey) / SUB;
      if (this.lastError > 24) { this.smoothX = this.smoothY = 0; this.snaps++; }   // far off: jump
      else { this.smoothX += ex; this.smoothY += ey; this.prev.x -= ex; this.prev.y -= ey; }
    }
    this.state = s;
  }

  /** Where to draw this frame, in pixels: between the last two ticks, plus the fading correction offset. */
  render(alpha: number, dt: number): { x: number; y: number; z: number } {
    const k = Math.pow(0.0005, dt);   // ~90% of an error gone in 0.3 s
    this.smoothX *= k; this.smoothY *= k;
    this.smoothZ *= k;
    if (Math.abs(this.smoothX) < 4) this.smoothX = 0; if (Math.abs(this.smoothY) < 4) this.smoothY = 0; if (Math.abs(this.smoothZ) < 4) this.smoothZ = 0;
    return {
      x: (this.prev.x + (this.state.x - this.prev.x) * alpha + this.smoothX) / SUB,
      y: (this.prev.y + (this.state.y - this.prev.y) * alpha + this.smoothY) / SUB,
      z: Math.max(0, (this.prev.z + (this.state.z - this.prev.z) * alpha + this.smoothZ) / SUB),
    };
  }

  /** Battle circles you may not walk into (the server keeps you out the same way). */
  circles: { x: number; y: number; r: number }[] = [];
  /** Standing still in a battle: inputs don't move you. */
  frozen = false;
  private step(s: MoveState, bits: number): void {
    if (this.frozen) return;
    stepMove(s, bits, this.map);
    if (this.circles.length && !s.flying) pushOutOfCircles(s, this.circles, (x, y, z, f) => this.map.fitsSub(x, y, z, f));
  }

  /** A new zone: the rules now use its walls. */
  setMap(map: ZoneMap): void { this.map = map; }

  resetTo(state: MoveState, seq: number): void {
    this.state = cloneMove(state); this.prev = cloneMove(state); this.seq = seq; this.pending = []; this.lastAck = -1; this.smoothX = this.smoothY = this.smoothZ = 0;
  }
  get unacked(): number { return this.pending.length; }
}
